// Đánh giá quyền thuần (không phụ thuộc framework) — dùng ở identity-service (tính quyền hiệu lực),
// service (kiểm quyền trong handler, lọc phạm vi ở repository) và web (ẩn/hiện thao tác).
import type { AccountStatus, Actor, Grant, Scope } from './context.js';
import { PERMISSIONS, permissionDef, type RoleTemplate } from './permissions.js';

const RANK: Record<Scope, number> = { own: 1, team: 2, all: 3 };

/**
 * Quyền hiệu lực của một vai trò.
 * - Tài khoản locked/left, vai trò null/đã xoá ⇒ rỗng.
 * - perms '*' ⇒ mọi khoá trong danh mục (scoped ⇒ 'all', thường ⇒ true).
 * - Quyền scoped mang true ⇒ 'all'; quyền thường mang phạm vi ⇒ true; khoá ngoài danh mục bị bỏ.
 */
export function resolveGrants(role: Pick<RoleTemplate, 'perms'> | null | undefined, status: AccountStatus | 'none' = 'active'): Record<string, Grant> {
  if (!role || status === 'locked' || status === 'left') return {};
  if (role.perms === '*') return Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.scoped ? 'all' : true] as const));
  const out: Record<string, Grant> = {};
  for (const [k, v] of Object.entries(role.perms)) {
    if (!v) continue;
    const def = permissionDef(k);
    if (!def) continue;
    out[k] = def.scoped ? (v === true ? 'all' : v) : true;
  }
  return out;
}

type GrantsHolder = Pick<Actor, 'grants'> | { grants: Record<string, Grant> };

export const scopeOf = (actor: GrantsHolder | null, perm: string): Scope | null => {
  const v = actor?.grants[perm];
  if (!v) return null;
  return v === true ? 'all' : v;
};

export const atLeast = (actor: GrantsHolder | null, perm: string, scope: Scope) => (RANK[scopeOf(actor, perm) as Scope] ?? 0) >= RANK[scope];

/** Chủ sở hữu bản ghi để đối chiếu phạm vi: ownerId ?? staffId. */
export interface Owned {
  ownerId?: string | null;
  staffId?: string | null;
}
export const ownerOf = (rec: Owned) => rec.ownerId ?? rec.staffId ?? null;

type ActorLike = Pick<Actor, 'grants' | 'staffId' | 'team'>;

/** can(actor, perm) — có quyền ở bất kỳ phạm vi; can(actor, perm, record) — có quyền với chính bản ghi đó. */
export function can(actor: ActorLike | null, perm: string, rec?: Owned): boolean {
  if (!actor) return true; // ngữ cảnh hệ thống (job, saga)
  const v = actor.grants[perm];
  if (!v) return false;
  if (rec === undefined || v === true || v === 'all') return true;
  const owner = ownerOf(rec);
  if (!owner || !actor.staffId) return false;
  if (v === 'own') return owner === actor.staffId;
  return owner === actor.staffId || actor.team.includes(owner);
}

/** Bộ lọc phạm vi cho truy vấn: 'all' = không lọc; mảng id nhân sự = owner_id IN (...); [] = không thấy gì. */
export function scopeFilter(actor: ActorLike | null, perm: string): 'all' | string[] {
  if (!actor) return 'all';
  const sc = scopeOf(actor, perm);
  if (!sc || !actor.staffId) return sc === 'all' ? 'all' : [];
  if (sc === 'all') return 'all';
  if (sc === 'team') return Array.from(new Set([actor.staffId, ...actor.team]));
  return [actor.staffId];
}

/**
 * Phạm vi "nhóm": {chính tôi} ∪ thành viên (mọi trạng thái, kể cả đã nghỉ) của mọi nhóm T
 * với T là nhóm tôi thuộc về, hoặc tôi là trưởng nhóm / quản lý của T.
 */
export function teamStaffIds(
  self: { staffId: string; teamId: string | null },
  teams: { id: string; leaderId: string | null; managerId: string | null }[],
  employees: { staffId: string; teamId: string | null }[],
): string[] {
  const mine = new Set(teams.filter((t) => t.id === self.teamId || t.leaderId === self.staffId || t.managerId === self.staffId).map((t) => t.id));
  if (self.teamId) mine.add(self.teamId);
  const out = new Set([self.staffId]);
  for (const e of employees) if (e.teamId && mine.has(e.teamId)) out.add(e.staffId);
  return [...out];
}

/** Vai trò có ≥1 quyền cấp quản trị ⇒ cần mức truy cập quản trị (Editor), ngược lại Contributor. */
export function requiredLevel(role: Pick<RoleTemplate, 'perms'> | null | undefined): 'admin' | 'interact' {
  const g = resolveGrants(role);
  return Object.keys(g).some((k) => permissionDef(k)?.level === 'admin') ? 'admin' : 'interact';
}

/** Người bán = tài khoản active có quyền giữ căn (không phụ thuộc đã liên kết đăng nhập hay chưa). */
export const isSeller = (a: { status: AccountStatus | 'none'; grants: Record<string, Grant> }) => a.status === 'active' && !!a.grants['booking.lock'];

/** Rút gọn quyền cho JWT: own/team/all ⇒ o/t/a, true ⇒ 1. */
export function compactGrants(g: Record<string, Grant>): Record<string, 'o' | 't' | 'a' | 1> {
  const out: Record<string, 'o' | 't' | 'a' | 1> = {};
  for (const [k, v] of Object.entries(g)) out[k] = v === true ? 1 : (v[0] as 'o' | 't' | 'a');
  return out;
}
export function expandGrants(c: Record<string, 'o' | 't' | 'a' | 1>): Record<string, Grant> {
  const M = { o: 'own', t: 'team', a: 'all' } as const;
  const out: Record<string, Grant> = {};
  for (const [k, v] of Object.entries(c)) out[k] = v === 1 ? true : M[v];
  return out;
}
